import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const faqs = [
  {
    category: "Services",
    questions: [
      {
        q: "What is a fractional CISO?",
        a: "A fractional CISO (also known as a virtual CISO or vCISO) provides strategic security leadership on a part-time or contract basis. You get the expertise of a Chief Information Security Officer without the cost of a full-time executive salary (typically £100k-150k+). Our fractional CISO service includes monthly strategy sessions, quarterly board reports, incident response support, and ongoing security guidance tailored to your business needs.",
        cta: { text: "Learn more about fractional CISO services", link: "/services/vciso" },
      },
      {
        q: "How long does Cyber Essentials certification take?",
        a: "The typical Cyber Essentials certification process takes 4-6 weeks. Week 1-2 covers gap analysis, Week 3-4 focuses on remediation, and Week 5-6 involves the assessment and certification. We have a 98% first-time pass rate for organisations following our preparation program.",
        cta: { text: "Download our Cyber Essentials Preparation Guide", link: "/resources" },
      },
      {
        q: "What's included in a security risk assessment?",
        a: "Our risk assessments follow ISO 27005 and NIST frameworks and include: comprehensive threat analysis, vulnerability identification, business impact assessment, risk prioritization with heat maps, and detailed treatment plans with cost-benefit analysis. Typical engagements take 2-4 weeks and cover people, processes, technology, and third-party risks.",
        cta: { text: "View Risk Management services", link: "/services/risk-management" },
      },
    ],
  },
  {
    category: "Pricing & Process",
    questions: [
      {
        q: "How much do your services cost?",
        a: "Pricing varies based on your organisation's size, industry, and specific needs. As a guide: fractional CISO services start from £2,500/month, risk assessments range from £5,000-15,000, and Cyber Essentials certification support is typically £2,000-4,000. We offer free initial consultations to provide accurate quotes.",
        cta: { text: "Book a free consultation", link: "https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" },
      },
      {
        q: "What happens during the initial consultation?",
        a: "Our 30-minute consultation is a no-pressure conversation where we: discuss your current security challenges, understand your business goals and compliance requirements, identify immediate risks or gaps, and recommend appropriate next steps. There's no obligation to proceed, and you'll receive valuable insights regardless.",
        cta: { text: "Schedule your consultation", link: "https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" },
      },
      {
        q: "Do you offer ongoing support or just one-time projects?",
        a: "We offer both! Many clients start with a specific project (like Cyber Essentials or a risk assessment) and then engage us for ongoing support through fractional CISO services, managed security, or quarterly health checks. We're flexible and can scale our engagement to match your needs and budget.",
        cta: { text: "Explore our services", link: "/#services" },
      },
    ],
  },
  {
    category: "Security & Compliance",
    questions: [
      {
        q: "What's the difference between Cyber Essentials and Cyber Essentials Plus?",
        a: "Cyber Essentials is a self-assessment verified by an independent body. Cyber Essentials Plus includes hands-on technical verification where an assessor tests your systems. Plus is required for certain government contracts and provides higher assurance. We can help you determine which certification level you need.",
        cta: { text: "Download Cyber Essentials comparison guide", link: "/resources" },
      },
      {
        q: "How do I know if I need NIST or CAF assessments?",
        a: "NIST CSF is ideal for organisations seeking a flexible, widely-recognised framework that aligns with international standards. CAF is specifically designed for critical national infrastructure and supply chain organisations in the UK. Both provide structured approaches to measuring and improving security maturity.",
        cta: { text: "Take our 2-minute security assessment", link: "/assessment" },
      },
      {
        q: "Can you help with GDPR compliance?",
        a: "Yes! While we don't provide legal advice, we help implement the technical and organisational measures required for GDPR compliance: data protection by design, security controls, breach detection and response, vendor management, and documentation. We work alongside your legal team or DPO to ensure comprehensive coverage.",
        cta: { text: "Contact us about GDPR support", link: "#contact" },
      },
    ],
  },
  {
    category: "Getting Started",
    questions: [
      {
        q: "We're a small business with limited budget. Where should we start?",
        a: "Start with our free security assessment to identify your biggest risks. Then consider Cyber Essentials certification (£2-4k) as a cost-effective foundation. Many businesses also benefit from a one-off risk assessment (from £5k) to create a prioritized security roadmap. We can help you build security incrementally within your budget.",
        cta: { text: "Take free security assessment", link: "/assessment" },
      },
      {
        q: "Do we need to be technical to work with you?",
        a: "Not at all! We specialise in making security accessible to non-technical business leaders. We explain everything in plain English, avoid jargon, and focus on business outcomes. If you have technical staff, great—we'll work with them too. If not, we can handle everything or recommend trusted IT partners.",
        cta: { text: "See how we work", link: "/about" },
      },
      {
        q: "How quickly can you start if we have an urgent need?",
        a: "For urgent situations (active incidents, imminent audits, etc.), we can typically mobilize within 24-48 hours. For standard engagements, we usually begin within 1-2 weeks of contract signature. Our goal is to be as responsive as your situation requires.",
        cta: { text: "Get urgent support", link: "https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" },
      },
    ],
  },
];

const FAQ = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.flatMap(category => 
      category.questions.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    )
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Cyber Security FAQ | 4FOX Security"
        description="Common questions about cyber security services, fractional CISO (vCISO), Cyber Essentials certification, risk assessments, pricing, and getting started with security consulting."
        canonical="https://4fox.uk/faq"
        schema={faqSchema}
      />
      <Navbar />
      
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Everything you need to know about our cyber security services. 
              Can't find what you're looking for? We're here to help.
            </p>
            <a 
              href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg">
                Book a Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>

          {/* FAQ Content */}
          <div className="max-w-4xl mx-auto space-y-12">
            {faqs.map((category, idx) => (
              <div key={idx}>
                <h2 className="text-3xl font-bold mb-6">{category.category}</h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, qIdx) => (
                    <AccordionItem key={qIdx} value={`${idx}-${qIdx}`} className="bg-card border rounded-lg px-6 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <AccordionTrigger className="text-left font-semibold hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        <p className="mb-4">{faq.a}</p>
                        {faq.cta && (
                          <a 
                            href={faq.cta.link} 
                            target={faq.cta.link.startsWith('http') ? '_blank' : '_self'}
                            rel={faq.cta.link.startsWith('http') ? 'noopener noreferrer' : ''}
                          >
                            <Button variant="link" className="pl-0">
                              {faq.cta.text}
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </a>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          {/* Still Have Questions CTA */}
          <div className="bg-muted rounded-lg p-8 text-center mt-16 max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              We're happy to answer any questions you have about our services, 
              pricing, or how we can help secure your organisation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://outlook.office365.com/owa/calendar/HazelMcPherson4FOXSecurity@4fox.uk/bookings/" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg">
                  Book Free Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default FAQ;
